import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './LandingPage3.module.css';

const LandingPage3 = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionsRef = useRef({});
  const [activeStep, setActiveStep] = useState(1);
  const timerRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionsRef.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-advance steps every 10 seconds
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Start new timer
    timerRef.current = setInterval(() => {
      setActiveStep((prev) => (prev === 4 ? 1 : prev + 1));
    }, 10000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [activeStep]); // Restart timer when activeStep changes

  const handleStepClick = (step) => {
    setActiveStep(step);
    // The useEffect will automatically clear and restart the timer
  };

  const addToRefs = (id) => (el) => {
    if (el) sectionsRef.current[id] = el;
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={styles.container}>
      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContent}>
          <Link to="/" className={styles.logo}>
            <img src="/icons/autochef_logo.svg" alt="AutoChef" />
            <span>AutoChef</span>
          </Link>
          <div className={styles.navButtons}>
            <Link to="/signup" className={styles.navButton}>Start Free Trial</Link>
            <Link to="/login" className={styles.navButton} style={{backgroundColor: '#fff', color: '#52cd92', border:'1px solid #52cd92'}}>Login</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroGrid}>
          {/* Left Column - Text Content */}
          <div className={styles.heroLeft}>
            <h1 className={styles.heroTitle}>
              {/* Your meals on <em className={styles.autopilot}>autopilot</em> */}
              Take meal planning off your plate.
            </h1>
            <p className={styles.heroSubtitle}>
              Take the stress out of the daily "what's for dinner?" question. 
              Save time, money, and reduce food waste.
            </p>
            <div className={styles.heroButtons}>
              <Link to="/signup">
                <button className={styles.heroCta}>
                  Get Started Free <span className={styles.arrow}>→</span>
                </button>
              </Link>
              <button 
                className={styles.secondaryCta}
                onClick={() => scrollToSection('how-it-works')}
              >
                How It Works
              </button>
            </div>

            {/* Social Proof */}
            {/* <div className={styles.socialProof}>
              <div className={styles.avatars}>
                <div className={styles.avatar}><img src="/images/avatar1.png" alt="Avatar 1" /></div>
                <div className={styles.avatar}><img src="/images/avatar2.jpg" alt="Avatar 2" /></div>
                <div className={styles.avatar}><img src="/images/avatar3.jpg" alt="Avatar 3" /></div>
                <div className={styles.avatar}><img src="/images/avatar4.jpeg" alt="Avatar 4" /></div>
              </div>
              <p className={styles.socialText}>
                Join others who have stopped stressing about meals
              </p>
            </div> */}
          </div>

          {/* Right Column - Phone Mockup */}
          <div className={styles.heroRight}>
            <img 
            //   src="/images/MealPlanPageiOS.svg" 
            src="images/AutoChefwithMealsSVG.png"
              alt="AutoChef App - This Week's Meals" 
              className={styles.mockupImage}
            />
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section 
        id="benefits"
        ref={addToRefs('benefits')}
        className={`${styles.benefits} ${isVisible.benefits ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <div className={styles.benefitsHeader}>
            <h2 className={styles.benefitsTitle}>
              From kitchen chaos to mealtime mastery.
            </h2>
            <h2 className={styles.benefitsTitle}>In minutes.</h2>
            <div className={styles.benefitsSubheader}>
              {/* <p className={styles.benefitsSubtext}>With my help, of course.</p>
              <img src="/icons/byte.png" alt="Byte the AutoChef Robot" className={styles.robotMascot} /> */}
            </div>
          </div>

          <div className={styles.benefitsGrid}>
            {/* Block 1 */}
            <div className={styles.benefitBlock}>
              <div className={styles.benefitIcon}>🍽️</div>
              <h3 className={styles.benefitTitle}>Never wonder what to cook again</h3>
              <p className={styles.benefitDescription}>
                We've officially retired your nightly "What's for dinner?" crisis. Tell me AutoChef 
                preferences (vegan, keto, gluten-free), and I'll whip up meal 
                plans that actually match your life.
              </p>
            </div>

            {/* Block 2 */}
            <div className={styles.benefitBlock}>
              <div className={styles.benefitIcon}>📝</div>
              <h3 className={styles.benefitTitle}>Grocery lists that build themselves</h3>
              <p className={styles.benefitDescription}>
                Your grocery list now updates itself every time you choose a recipe. No 
                more wandering the aisles trying to remember if you have garlic at home. I checked: you 
                don't.
              </p>
            </div>

            {/* Block 3 */}
            <div className={styles.benefitBlock}>
              <div className={styles.benefitIcon}>💰</div>
              <h3 className={styles.benefitTitle}>Stop wasting food and money</h3>
              <p className={styles.benefitDescription}>
                AutoChef helps you use what you already have and avoid over-buying so your wilted 
                spinach era can finally come to an end.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section 
        id="how-it-works"
        ref={addToRefs('how-it-works')}
        className={`${styles.howItWorks} ${isVisible['how-it-works'] ? styles.visible : ''}`}
        style={{ backgroundColor: '#243029', color: '#FAFBFF' }}
      >
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle} style={{ color: '#FAFBFF' }}>How to AutoChef</h2>
          
          {/* Step Selector */}
          <div className={styles.stepSelector}>
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className={styles.stepButtonWrapper}>
                {activeStep === step && (
                  <svg key={`progress-${activeStep}`} className={styles.progressRing} viewBox="0 0 64 64">
                    <circle
                      className={styles.progressRingCircle}
                      stroke="#FF6652"
                      strokeWidth="3"
                      fill="transparent"
                      r="29"
                      cx="32"
                      cy="32"
                    />
                  </svg>
                )}
                <button
                  className={`${styles.stepButton} ${activeStep === step ? styles.stepButtonActive : ''}`}
                  onClick={() => handleStepClick(step)}
                >
                  {step}
                </button>
              </div>
            ))}
          </div>

          {/* Active Step Content */}
          <div className={styles.stepContainer}>
            <div className={styles.stepRow}>
              <div className={styles.stepContent}>
                {activeStep === 1 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Set Your Preferences</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Tell us about your dietary needs, cuisine preferences, allergens and family size. 
                      We'll use this to suggest meals that you'll love.
                    </p>
                  </>
                )}
                {activeStep === 2 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Review Your Meals</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Get personalized meal suggestions that use what you already have.
                      Add, change, or remove meals with a single tap.
                    </p>
                  </>
                )}
                {activeStep === 3 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Shop with Smart Lists</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Dynamic grocery lists based on what you already have. Check off items as you shop with our mobile-friendly interface.
                    </p>
                  </>
                )}
                {activeStep === 4 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Cook & Enjoy</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Easy to follow step-by-step instructions. Need to adjust the serving size last minute? No problem.
                    </p>
                  </>
                )}
              </div>
              
              <div className={styles.stepImage}>
                {activeStep === 1 && (
                  <img src="/images/SetYourPreferences.png" alt="Diet Preferences" className={styles.stepVideo} />
                )}
                {activeStep === 2 && (
                  <img src="/images/ReviewYourMeals1.png" alt="Meal Planning" className={styles.stepVideo} />
                )}
                {activeStep === 3 && (
                  <img src="/images/GroceryList1.png" alt="Grocery List" className={styles.stepVideo} />
                )}
                {activeStep === 4 && (
                  <img src="/images/SetYourPreferences1.png" alt="Recipe View" className={styles.stepVideo} />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        ref={addToRefs('faq')}
        className={`${styles.faq} ${isVisible.faq ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Questions? We've Got Answers.</h2>
          {/* <p className={styles.faqSubtitle}>
            <img src="/icons/byte.png" alt="Byte" className={styles.faqByte} />
            Here's what humans usually ask me:
          </p> */}

          <div className={styles.faqList}>
            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 1 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
              >
                <span>Do I really need another app?</span>
                <span className={styles.faqIcon}>{openFaq === 1 ? '−' : '+'}</span>
              </button>
              {openFaq === 1 && (
                <div className={styles.faqAnswer}>
                  <p>Fair question! But here's the thing: I'm not just another app. I'm your personal meal planning assistant who actually helps you stop wasting food, save money, and eat better. Plus, I don't judge your 3am leftover pizza decisions. Try the free trial—what's the worst that could happen? (Spoiler: You'll probably eat better.)</p>
                </div>
              )}
            </div>

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 2 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
              >
                <span>How much time will this actually save me?</span>
                <span className={styles.faqIcon}>{openFaq === 2 ? '−' : '+'}</span>
              </button>
              {openFaq === 2 && (
                <div className={styles.faqAnswer}>
                  <p>Most people save 3-5 hours per week. That's the time you'd normally spend wondering what to cook, making multiple grocery store trips, and searching for recipes. I do all of that in seconds. You're welcome.</p>
                </div>
              )}
            </div>

            {/* <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 3 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
              >
                <span>Can I use my own recipes?</span>
                <span className={styles.faqIcon}>{openFaq === 3 ? '−' : '+'}</span>
              </button>
              {openFaq === 3 && (
                <div className={styles.faqAnswer}>
                  <p>Absolutely! Add your grandma's secret sauce, your famous chili recipe, or that thing you invented at 2am. I'll even help you organize them and add them to your meal plans. Your recipes, my organization skills—dream team.</p>
                </div>
              )}
            </div> */}

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 4 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}
              >
                <span>What if I have dietary restrictions?</span>
                <span className={styles.faqIcon}>{openFaq === 4 ? '−' : '+'}</span>
              </button>
              {openFaq === 4 && (
                <div className={styles.faqAnswer}>
                  <p>I've got you covered! Vegan, vegetarian, pescatarian, gluten-free, dairy-free, nut allergies—whatever you need. Just tell me your restrictions during setup, and I'll only suggest meals that work for you. No awkward "oops, that has dairy" moments.</p>
                </div>
              )}
            </div>

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 5 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 5 ? null : 5)}
              >
                <span>How much does it cost?</span>
                <span className={styles.faqIcon}>{openFaq === 5 ? '−' : '+'}</span>
              </button>
              {openFaq === 5 && (
                <div className={styles.faqAnswer}>
                  <p>Just $3.99/month after your 14-day free trial. That's less than two fancy coffees, and you'll save way more than that on groceries every month. No credit card needed to start the trial—I'm confident you'll love me.</p>
                </div>
              )}
            </div>

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 6 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 6 ? null : 6)}
              >
                <span>Can I cancel anytime?</span>
                <span className={styles.faqIcon}>{openFaq === 6 ? '−' : '+'}</span>
              </button>
              {openFaq === 6 && (
                <div className={styles.faqAnswer}>
                  <p>Yes! Cancel anytime, no questions asked. Though I'll be sad to see you go. (But seriously, no hassle, no hidden fees, no "talk to our retention team" nonsense.)</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      {/* <section 
        id="features"
        ref={addToRefs('features')}
        className={`${styles.features} ${isVisible.features ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>
            Why AutoChef?
          </h2>

          <div className={styles.featureGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🎯</div>
              <h3>Smart Matching</h3>
              <p>AI suggests meals based on your pantry, preferences, and cooking history</p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>♻️</div>
              <h3>Zero Waste</h3>
              <p>Use ingredients across multiple recipes. Nothing goes to waste</p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>⚡</div>
              <h3>Totally Flexible</h3>
              <p>Swap meals, adjust portions, cook on your schedule</p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>💰</div>
              <h3>Save Money</h3>
              <p>Save up to 60% compared to meal kit services</p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>👨‍👩‍👧</div>
              <h3>Family Friendly</h3>
              <p>Scale recipes for any size. Kid-friendly options included</p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>📱</div>
              <h3>Works Everywhere</h3>
              <p>Seamless sync across phone, tablet, and desktop</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Comparison Section */}
      {/* <section 
        id="comparison"
        ref={addToRefs('comparison')}
        className={`${styles.comparison} ${isVisible.comparison ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Why Pay More?</h2>
          
          <div className={styles.comparisonTable}>
            <div className={styles.comparisonColumn}>
              <h3 className={styles.comparisonTitle}>Meal Kits</h3>
              <div className={styles.comparisonPrice}>$12-15<span>/meal</span></div>
              <ul className={styles.comparisonList}>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Rigid weekly menus</span>
                </li>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Fixed delivery schedules</span>
                </li>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Excessive packaging</span>
                </li>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Limited customization</span>
                </li>
              </ul>
            </div>

            <div className={styles.comparisonColumnHighlight}>
              <div className={styles.bestValueBadge}>BEST VALUE</div>
              <h3 className={styles.comparisonTitle}>AutoChef</h3>
              <div className={styles.comparisonPrice}>$4-6<span>/meal</span></div>
              <ul className={styles.comparisonList}>
                <li className={styles.comparisonPositive}>
                  <span className={styles.icon}>✓</span>
                  <span>500+ recipes to choose from</span>
                </li>
                <li className={styles.comparisonPositive}>
                  <span className={styles.icon}>✓</span>
                  <span>Shop on your schedule</span>
                </li>
                <li className={styles.comparisonPositive}>
                  <span className={styles.icon}>✓</span>
                  <span>Use what you already have</span>
                </li>
                <li className={styles.comparisonPositive}>
                  <span className={styles.icon}>✓</span>
                  <span>Complete flexibility</span>
                </li>
              </ul>
              <Link to="/signup">
                <button className={styles.comparisonCta}>Start Saving Now</button>
              </Link>
            </div>

            <div className={styles.comparisonColumn}>
              <h3 className={styles.comparisonTitle}>Winging It</h3>
              <div className={styles.comparisonPrice}>$8-10<span>/meal</span></div>
              <ul className={styles.comparisonList}>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Daily decision fatigue</span>
                </li>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Forgotten ingredients</span>
                </li>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Same meals on repeat</span>
                </li>
                <li className={styles.comparisonNegative}>
                  <span className={styles.icon}>✕</span>
                  <span>Impulse grocery buys</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section> */}

      {/* Testimonials */}
      {/* <section 
        id="testimonials"
        ref={addToRefs('testimonials')}
        className={`${styles.testimonials} ${isVisible.testimonials ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>What People Are Saying</h2>

          <div className={styles.testimonialGrid}>
            <div className={styles.testimonialCard}>
              <div className={styles.stars}>★★★★★</div>
              <p className={styles.testimonialText}>
                "We went from spending $800/month on groceries and takeout to $450. 
                Same amount of meals, way less stress."
              </p>
              <div className={styles.testimonialAuthor}>
                <div className={styles.testimonialAvatar}>JS</div>
                <div>
                  <div className={styles.authorName}>Jordan S.</div>
                  <div className={styles.authorRole}>Parent of 3</div>
                </div>
              </div>
            </div>

            <div className={styles.testimonialCard}>
              <div className={styles.stars}>★★★★★</div>
              <p className={styles.testimonialText}>
                "I actually enjoy cooking now. No more panic at 5pm wondering what's for dinner."
              </p>
              <div className={styles.testimonialAuthor}>
                <div className={styles.testimonialAvatar}>MK</div>
                <div>
                  <div className={styles.authorName}>Michael K.</div>
                  <div className={styles.authorRole}>Working professional</div>
                </div>
              </div>
            </div>

            <div className={styles.testimonialCard}>
              <div className={styles.stars}>★★★★★</div>
              <p className={styles.testimonialText}>
                "Finally cancelled our meal kit subscription. This is so much better and 
                saves us over $200 a month."
              </p>
              <div className={styles.testimonialAuthor}>
                <div className={styles.testimonialAvatar}>SM</div>
                <div>
                  <div className={styles.authorName}>Sarah M.</div>
                  <div className={styles.authorRole}>Busy parent</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Final CTA */}
      <section className={styles.finalCta}>
        <div className={styles.finalCtaContent}>
          <h2 className={styles.finalCtaTitle}>
            Ready to put your meals on autopilot?
          </h2>
          
          <div className={styles.pricingBox}>
            <div className={styles.pricingHighlight}>7 Days Free</div>
            <div className={styles.pricingDetails}>Then just $3.99/month</div>
            <Link to="/signup">
              <button className={styles.finalCtaButton}>Start Your Free Trial</button>
            </Link>
            <div className={styles.pricingBenefits}>
              <span>✓ No credit card required</span>
              <span>✓ Cancel anytime</span>
              <span>✓ Full access to all features</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img src="/icons/autochef_logo.svg" alt="AutoChef" />
            <span>AutoChef</span>
          </div>
          <div className={styles.footerLinks}>
            <Link to="/about">About</Link>
            <Link to="/help">Help</Link>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <div className={styles.footerCopy}>
            © {new Date().getFullYear()} AutoChef. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage3;
